// The pages scroll inside `.app-main`, not the window, so the browser's own
// scroll restoration never applies to them: a page opened from deep in a
// long document would start at that document's offset. Opening another page
// starts at the top; going back or forward returns to where that entry was
// left, once its content (which may still be loading) is tall enough. A
// change of hash or query on the same page (or between Needs you and one of
// its requests) leaves the scroll alone.
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { pageOf } from '../lib/keys'

/** How long a restore waits for the page to grow tall enough. */
export const RESTORE_WAIT_MS = 2_000
const RESTORE_POLL_MS = 50
const USER_INPUT = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const

export function useScrollMemory(ref: RefObject<HTMLElement | null>): void {
  const location = useLocation()
  const navigationType = useNavigationType()
  const offsets = useRef(new Map<string, number>())
  const shown = useRef({ key: location.key, pathname: location.pathname })

  // Every scroll records the offset of the entry on screen.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onScroll = () => offsets.current.set(shown.current.key, el.scrollTop)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [ref])

  useLayoutEffect(() => {
    const el = ref.current
    const previous = shown.current
    shown.current = { key: location.key, pathname: location.pathname }
    if (!el || previous.key === location.key || pageOf(previous.pathname) === pageOf(location.pathname)) return

    const target = navigationType === 'POP' ? (offsets.current.get(location.key) ?? 0) : 0
    el.scrollTop = target
    if (target === 0 || el.scrollTop >= target - 1) return

    // Not tall enough yet: follow the page as it loads, until the reader moves.
    let timer: ReturnType<typeof setInterval> | null = null
    const stop = () => {
      if (timer !== null) clearInterval(timer)
      timer = null
      for (const type of USER_INPUT) el.removeEventListener(type, stop)
    }
    const started = Date.now()
    timer = setInterval(() => {
      el.scrollTop = target
      if (el.scrollTop >= target - 1 || Date.now() - started > RESTORE_WAIT_MS) stop()
    }, RESTORE_POLL_MS)
    for (const type of USER_INPUT) el.addEventListener(type, stop, { passive: true })
    return stop
  }, [location.key, location.pathname, navigationType, ref])
}
